/**
 * Arrow-key navigation (08 §5.5): focus moves to the nearest control in the direction pressed,
 * judged by where things are on screen, so lists, grids and the theatre map all work without
 * each screen wiring its own neighbours. Tab order is the browser's own.
 */

export type Direction = 'up' | 'down' | 'left' | 'right';

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A sideways miss costs this many times a forward gap: prefer what is straight ahead. */
const CROSS_WEIGHT = 2.5;

/**
 * Index of the best box to move to from `from`, or -1. A candidate must lie beyond the leading
 * edge's near side in the direction of travel; among those the cheapest forward gap plus
 * weighted sideways offset wins. Pure.
 */
export function pickInDirection(from: Box, boxes: readonly Box[], dir: Direction): number {
  const fx = from.left + from.width / 2;
  const fy = from.top + from.height / 2;
  let best = -1;
  let bestCost = Infinity;
  boxes.forEach((b, i) => {
    const cx = b.left + b.width / 2;
    const cy = b.top + b.height / 2;
    const dx = cx - fx;
    const dy = cy - fy;
    const forward = dir === 'left' ? -dx : dir === 'right' ? dx : dir === 'up' ? -dy : dy;
    if (forward <= 0.5) return;
    const horizontal = dir === 'left' || dir === 'right';
    // Overlap on the cross axis means "in line": no sideways cost at all.
    const lo = horizontal ? Math.max(from.top, b.top) : Math.max(from.left, b.left);
    const hi = horizontal
      ? Math.min(from.top + from.height, b.top + b.height)
      : Math.min(from.left + from.width, b.left + b.width);
    const cross = hi > lo ? 0 : Math.abs(horizontal ? dy : dx);
    const cost = forward + cross * CROSS_WEIGHT;
    if (cost < bestCost) {
      bestCost = cost;
      best = i;
    }
  });
  return best;
}

const FOCUSABLE = 'button, input, [tabindex]';

function usable(node: HTMLElement): boolean {
  if ((node as HTMLButtonElement).disabled === true) return false;
  if (node.getAttribute('tabindex') === '-1') return false;
  const r = node.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** Every control in `scope` that can take focus now, in document order. */
export function focusables(scope: HTMLElement): HTMLElement[] {
  return Array.from(scope.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(usable);
}

/** Focus the first usable control in `scope`; returns whether anything took it. */
export function focusFirst(scope: HTMLElement): boolean {
  const first = focusables(scope)[0];
  if (first === undefined) return false;
  first.focus({ preventScroll: false });
  return true;
}

/** Arrow keys belong to the field while typing (left/right) or dragging a slider. */
function keepsArrow(active: Element | null, dir: Direction): boolean {
  if (active === null || active.tagName !== 'INPUT') return false;
  const type = (active as HTMLInputElement).type;
  if (type === 'range') return dir === 'left' || dir === 'right';
  if (type === 'checkbox' || type === 'radio') return false;
  return dir === 'left' || dir === 'right';
}

/**
 * Move focus within `scope`. Returns true when the key was used (the caller then prevents the
 * default scroll). With nothing focused yet, any arrow lands on the first control.
 */
export function moveFocus(scope: HTMLElement, dir: Direction): boolean {
  const active = document.activeElement;
  if (keepsArrow(active, dir)) return false;
  const nodes = focusables(scope);
  if (nodes.length === 0) return false;
  const current = nodes.find((n) => n === active);
  if (current === undefined) {
    nodes[0]?.focus();
    return true;
  }
  const others = nodes.filter((n) => n !== current);
  const index = pickInDirection(
    current.getBoundingClientRect(),
    others.map((n) => n.getBoundingClientRect()),
    dir,
  );
  const next = others[index];
  if (next === undefined) return true;
  next.focus();
  next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  return true;
}
